import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { IParseProduct, PriceSourceType } from '../../models/quotations.models';

@Component({
  selector: 'app-price-details-tooltip',
  standalone: true,
  imports: [CommonModule, TranslateModule, SvgIconComponent],
  templateUrl: './price-details-tooltip.component.html',
  styleUrls: ['./price-details-tooltip.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PriceDetailsTooltipComponent {
  @Input() product!: IParseProduct;
  @Input() currencySymbol: string = '';
  @Input() viewMode: boolean = false;
  @Output() applyPrice = new EventEmitter<{ price: number; type: PriceSourceType }>();

  get currentPriceType(): PriceSourceType {
    return this.product?.price_type ?? 'catalog';
  }

  get isSystemApplied(): boolean {
    return this.currentPriceType === 'catalog';
  }

  get isAiApplied(): boolean {
    return this.currentPriceType === 'ai';
  }

  get isOverrideApplied(): boolean {
    return this.currentPriceType === 'override';
  }

  get isNoneApplied(): boolean {
    return this.currentPriceType === 'none';
  }

  /**
   * What the AI actually sent. When the sub-unit conversion fired, `ai_price`
   * is already re-expressed per selling unit (e.g. per pack), not what the AI
   * sent (e.g. per piece) — so this falls back to the true pre-conversion
   * snapshot (`ai_extracted_price`) instead. Applying the price still always
   * uses `ai_price` (the correct, converted, actionable value).
   */
  get displayedParsedPrice(): number | null | undefined {
    return this.product?.ai_unit_conversion_applied
      ? this.product.ai_extracted_price
      : this.product?.ai_price;
  }

  /** Shows a small note explaining why the parsed price differs from the applied price. */
  get showConversionNote(): boolean {
    return !!this.product?.ai_unit_conversion_applied;
  }

  onApplySystemPrice(event: Event): void {
    event.stopPropagation();
    if (this.product.catalog_price != null) {
      this.applyPrice.emit({ price: this.product.catalog_price, type: 'catalog' });
    }
  }

  onApplyAiPrice(event: Event): void {
    event.stopPropagation();
    if (this.product.ai_price != null) {
      this.applyPrice.emit({ price: this.product.ai_price, type: 'ai' });
    }
  }

  onApplyNoPrice(event: Event): void {
    event.stopPropagation();
    this.applyPrice.emit({ price: 0, type: 'none' });
  }

  formatPrice(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) return '--';
    return Number(value).toFixed(2) + ' ' + this.currencySymbol;
  }
}
